import React, { useMemo, useState } from "react";
import { TextField, Box, IconButton, Tooltip, InputAdornment } from "@material-ui/core";
import { Language, Warning } from "@material-ui/icons";
import { css } from "@emotion/css";
import { TranslateButton } from "shared/localizedText";
import { DEFAULT_LOCALE, LOCALES } from "../constants";
import { LocalizedText } from "../types";
import { readLocale, writeLocale } from "../utils";

type Props = {
  label: string;
  value: LocalizedText;
  onChange: (value: LocalizedText) => void;
  /** Which locale the main field edits. The expand section shows the rest. */
  activeLocale?: string;
  multiline?: boolean;
  rows?: number;
  disabled?: boolean;
  size?: "small" | "medium";
};

const TranslatedTextField: React.VFC<Props> = ({
  label,
  value,
  onChange,
  activeLocale,
  multiline = false,
  rows = 1,
  disabled = false,
  size = "medium",
}) => {
  const [expanded, setExpanded] = useState(false);

  const mainCode = activeLocale ?? DEFAULT_LOCALE;
  const mainMeta = LOCALES.find(m => m.code === mainCode) ?? LOCALES[0];
  const otherMetas = useMemo(() => LOCALES.filter(m => m.code !== mainCode), [mainCode]);

  const mainValue = readLocale(value, mainCode);
  const handleMain = (text: string) => onChange(writeLocale(value, mainCode, text));
  const handleOther = (code: string, text: string) => onChange(writeLocale(value, code, text));

  const hasOtherContent = otherMetas.some(m => readLocale(value, m.code).length > 0);
  // Flag missing translations only when there's source content to translate
  // from — an all-empty field isn't "missing" anything.
  const missingLocales = mainValue.trim()
    ? otherMetas.filter(m => !readLocale(value, m.code).trim()).map(m => m.label)
    : [];
  const hasMissingTranslations = missingLocales.length > 0;

  return (
    <Box>
      <TextField
        label={label}
        value={mainValue}
        onChange={e => handleMain(e.target.value)}
        fullWidth
        multiline={multiline}
        rows={rows}
        variant="outlined"
        margin="normal"
        size={size}
        disabled={disabled}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <span className={styles.flag}>{mainMeta.flag}</span>
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {otherMetas.length > 0 && (
                <>
                  <Tooltip title={expanded ? "Hide translations" : "Show translations"}>
                    <IconButton
                      size="small"
                      onClick={() => setExpanded(!expanded)}
                      className={
                        hasMissingTranslations
                          ? styles.missingTranslations
                          : hasOtherContent
                            ? styles.hasTranslations
                            : undefined
                      }
                    >
                      <Language fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {hasMissingTranslations && (
                    <Tooltip title={`Missing translations: ${missingLocales.join(", ")}`}>
                      <Warning fontSize="small" className={styles.missingTranslations} />
                    </Tooltip>
                  )}
                </>
              )}
            </InputAdornment>
          ),
        }}
      />
      {expanded && (
        <Box className={styles.translations}>
          {otherMetas.map(meta => (
            <TextField
              key={meta.code}
              value={readLocale(value, meta.code)}
              onChange={e => handleOther(meta.code, e.target.value)}
              fullWidth
              multiline={multiline}
              rows={rows}
              variant="outlined"
              size="small"
              disabled={disabled}
              placeholder={meta.label}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <span className={styles.flag}>{meta.flag}</span>
                  </InputAdornment>
                ),
                endAdornment: (
                  <TranslateButton
                    text={mainValue}
                    from={mainCode}
                    fromLabel={mainMeta.label}
                    to={meta.code}
                    onTranslated={text => handleOther(meta.code, text)}
                    disabled={disabled}
                  />
                ),
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default TranslatedTextField;

const styles = {
  translations: css`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 8px;
    margin-bottom: 16px;
    padding-left: 16px;
    border-left: 2px solid #e0e0e0;
  `,
  flag: css`
    font-size: 16px;
  `,
  hasTranslations: css`
    color: #1976d2;
  `,
  missingTranslations: css`
    color: #d32f2f;
  `,
};
